import React from 'react';
import Image from 'next/image';

const HowItWorksSection = ({ userType = 'default' }) => {
  const getSectionData = () => {
    switch (userType) {
      case 'designer':
        return {
          title: 'Bring your InDesign creativity to life, at scale.',
          description: 'Your designs deserve more than shortcuts or generic online editors. With Personalia, you work directly in Adobe InDesign — start from an existing design or build a new template from scratch. Once your template is ready, upload it to Personalia and we handle the rest: generating assets on-demand, at any scale, while preserving your fonts, colors, and layouts. No more managing dozens of design files for variations. No servers to manage, no software to install — just design once and let us do the heavy lifting. Here is how it works:',
          bgColor: 'bg-red-50'
        };
      case 'developer':
        return {
          title: 'Integrate pro-grade InDesign rendering into your workflows in minutes',
          description: 'Personalia turns your InDesign templates into a scalable API-driven rendering service. No rendering servers to build or maintain — we handle the infrastructure. Simply upload your template, call our API, and scale from one request to hundreds of thousands. Whether you are building marketing portals, powering eCommerce, or automating print production, Personalia fits directly into your stack. Here is how it works:',
          bgColor: 'bg-red-50'
        };
      default:
        return {
          title: 'From one template to unlimited on-brand assets in minutes',
          description: 'Personalia is the fastest way to turn an Adobe InDesign template into perfectly rendered PDFs, JPGs, or PNGs on demand. We handle the IT infrastructure and scalability — you focus on design. From one asset to hundreds of thousands, our cloud platform does the heavy lifting so you can scale without managing servers or software. Here is how it works:',
          bgColor: 'bg-gray-50'
        };
    }
  };

  const { title, description, bgColor } = getSectionData();

  return (
    <section className={`section ${bgColor}`}>
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            {description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Step 1 */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Image src="/images/icons/XMPie Personalia Icon 1.png" alt="Step 1" width={112} height={112} className="w-28 h-auto object-contain" />
            </div>
            <h3 className="text-xl font-bold mb-3">Design Templates</h3>
            <p className="text-gray-600 mb-4">
              Create data-ready templates in Adobe InDesign and use our personalization plugin to activate variable content.
            </p>
            <div className="flex justify-center mb-4">
              <Image src="/images/Personalia_InDesignPlugin.jpg" alt="Personalia InDesign Plugin" width={300} height={200} className="h-auto object-contain" />
            </div>
          </div>

          {/* Step 2 */}
          <div className="text-center relative">
            <div className="hidden md:block absolute top-8 -left-4 w-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <div className="flex justify-center mb-4">
              <Image src="/images/icons/XMPie Personalia Icon 2.png" alt="Personalia Plugin" width={112} height={112} className="w-28 h-auto object-contain" />
            </div>
            <h3 className="text-xl font-bold mb-3">Upload & Connect</h3>
            <p className="text-gray-600 mb-4">
              Package and upload your template to Personalia, then link it to your data workflows via API or built-in integrations.
            </p>
            <div className="flex justify-center mb-4">
              <Image src="/images/Personalia_Workspace.jpg" alt="Personalia Workspace" width={300} height={200} className="h-auto object-contain" />
            </div>
          </div>

          {/* Step 3 */}
          <div className="text-center relative">
            <div className="hidden md:block absolute top-8 -left-4 w-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <div className="flex justify-center mb-4">
              <Image src="/images/icons/XMPie Personalia Icon 3.png" alt="Step 3" width={112} height={112} className="w-28 h-auto object-contain" />
            </div>
            <h3 className="text-xl font-bold mb-3">Generate & Deliver</h3>
            <p className="text-gray-600 mb-4">
              Send data to generate unique PDFs, PNGs, or JPGs—ready to download, and handle within your process or workflow.
            </p>
            <div className="flex justify-center mb-4">
              <Image src="/images/Personalia_CURL.jpg" alt="Personalia CURL" width={300} height={200} className="h-auto object-contain" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
